import React, { memo, useMemo } from 'react';
import { Blok } from '../../interfaces/Blok.interface';
import { StoryblokAsset } from '../../interfaces/StoryblokAsset.interface';
import { StoryblokNode } from '../../interfaces/StoryblokNode.interface';
import Col from '../Utils/Col/Col';
import Image from '../Image/Image';
import Heading, { HeadingTypes } from '../Heading/Heading';
import CustomLink from '../CustomLink/CustomLink';
import classNames from 'classnames';
import { isEmailAddress } from '../../utils/isEmailAddress/isEmailAddress.util';
import { isTelephoneNumber } from '../../utils/isPhoneNumber/isPhoneNumber.util';
import { pushToDataLayer } from '../../utils/pushToDataLayer/pushToDataLayer.util';
import { StoryblokComponent } from 'gatsby-source-storyblok';
import SbImage from '../SbImage/SbImage';
import { loadavg } from 'os';

export interface ContactInformationOpeningHoursRangeProps extends Blok {
  day_label: string;
  opening_time: string;
  closing_time: string;
}

interface ContactOption extends Blok {
  text?: string;
  link?: {
    cached_url: string;
  };
  label?: string;
}
export interface ContactInformationContentProps extends Blok {
  icon?: StoryblokAsset;
  title: string;
  description?: string;
  contact_option?: ContactOption[];
  opening_hours: ContactInformationOpeningHoursRangeProps[];
  structured_data: Blok[];
}

interface ContactInformationProps {
  info: StoryblokNode<ContactInformationContentProps>;
  index_of_contact_information_item: number;
  number_of_contact_information_items: number;
}

const getContactNumberOrEmailWithAppropriatePrefix = (
  phoneNumberOfEmailAddress: string
): string => {
  const isEmail: boolean = /^\S+@\S+\.\S+$/.test(phoneNumberOfEmailAddress);
  if (isEmail) {
    return `mailto:${phoneNumberOfEmailAddress}`;
  } else {
    return `tel:${phoneNumberOfEmailAddress.replaceAll(' ', '')}`;
  }
};

const ContactInformation: React.FC<ContactInformationProps> = ({
  info: {
    content: {
      icon,
      title,
      description,
      contact_option,
      opening_hours,
      structured_data,
    },
    id,
  },
  index_of_contact_information_item,
  number_of_contact_information_items,
}) => {
  const handleDataLayerClickEvent = (contact_source: string): void => {
    const formattedContactSource: string = contact_source
      .toLowerCase()
      .replaceAll(' ', '_');
    pushToDataLayer({
      event: 'contact_us',
      data: { contact_source: formattedContactSource },
    });
  };

  return (
    <Col
      key={id}
      span={{
        sm: 10,
        md: 8,
        lg: 4,
        xl: number_of_contact_information_items === 3 ? 4 : 5,
      }}
      start={{
        sm: 2,
        md: 3,
        lg:
          number_of_contact_information_items === 3
            ? 'auto'
            : index_of_contact_information_item === 0
            ? 2
            : 8,
        xl:
          number_of_contact_information_items === 3
            ? 'auto'
            : index_of_contact_information_item === 0
            ? 1
            : 8,
      }}
    >
      {/*-- structured data --*/}
      {structured_data?.map((structuredData: Blok) => (
        <StoryblokComponent
          blok={{
            ...structuredData,
            contactType: title,
            contactAddress: contact_option
              ? contact_option[0].component === 'link'
                ? contact_option[0]?.link?.cached_url
                : contact_option[0]?.text
              : '',
          }}
          key={structuredData._uid}
        />
      ))}
      <div
        className={classNames(
          'h-full gap-y-6 flex flex-col items-center justify-between text-center',
          {
            'lg:px-10': number_of_contact_information_items === 3,
          }
        )}
      >
        {icon?.filename && (
          <div className="w-14 h-14">
            <SbImage
              asset={icon}
              options={{
                width: 56,
                height: 56,
                sizes: { base: 112 },
                loading: 'eager',
              }}
            />
          </div>
        )}
        <div className="flex-1 flex flex-col gap-y-4">
          <Heading text={title} headingType={HeadingTypes.h3} />
          {description && (
            <p className="text-text/body-tint flex-1">{description}</p>
          )}
          {contact_option &&
            contact_option.map((contactInfo) => {
              if (contactInfo.component === 'text' && contactInfo.text) {
                return (
                  <CustomLink
                    url={getContactNumberOrEmailWithAppropriatePrefix(
                      contactInfo.text
                    )}
                    label={contactInfo.text}
                    className={classNames(
                      'text-text/body-accent h-[45px] flex items-center justify-center font-bold',
                      {
                        'text-4xl': isTelephoneNumber(contactInfo.text),
                        'text-base': isEmailAddress(contactInfo.text),
                      }
                    )}
                    onClick={() => handleDataLayerClickEvent(title)}
                    key={contactInfo.text}
                  >
                    {contactInfo.text}
                  </CustomLink>
                );
              } else if (
                contactInfo.component === 'link' &&
                contactInfo.link?.cached_url
              ) {
                return (
                  <CustomLink
                    url={contactInfo.link.cached_url}
                    label={contactInfo.label}
                    className={classNames(
                      'text-text/body-accent h-[45px] flex items-center justify-center font-bold text-base'
                    )}
                    onClick={() => handleDataLayerClickEvent(title)}
                    key={contactInfo.label}
                  >
                    {contactInfo.label}
                  </CustomLink>
                );
              }
            })}
        </div>
        {opening_hours && (
          <ul className="w-full border-t border-t-border/default pt-6">
            {opening_hours.map(
              (
                opening_hours_range: ContactInformationOpeningHoursRangeProps
              ) => (
                <li
                  key={`${id}-${opening_hours_range._uid}`}
                  className="flex items-center justify-between text-text/body"
                >
                  <p>{opening_hours_range.day_label}</p>
                  <p>{`${opening_hours_range.opening_time} - ${opening_hours_range.closing_time}`}</p>
                </li>
              )
            )}
          </ul>
        )}
      </div>
    </Col>
  );
};

export default memo(ContactInformation);
